import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Button from '@material-ui/core/Button'
import {PaymentMethod} from '@stripe/stripe-js'
import {useBroadcasts} from 'lib/comms-api/events/broadcasts/list'
import {usePaymentMethods} from 'lib/event-api/stripe/payment_methods/list'
import {useRemovePaymentMethod} from 'lib/event-api/stripe/payment_methods/remove'
import {useSetDefaultPaymentMethod} from 'lib/event-api/stripe/payment_methods/set-default'
import ConfirmDialog from 'lib/ui/ConfirmDialog'
import {Loader} from 'lib/ui/Loader'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import {colors} from 'lib/ui/theme'
import {useDefaultPaymentMethod} from 'obvio/Billing/DefaultPaymentMethodProvider'
import {useOptionalPastDueRecovery} from 'obvio/Billing/PastDueRecoveryProvider'
import {getCardBrandDisplayName} from 'obvio/Billing/CreditCardSection/CardBrandLogo'
import {useGetDelinquentSubscription} from 'obvio/Billing/subscribe'
import {useBillingTransaction} from 'obvio/Billing/BillingTransactionProvider'
import React, {useEffect, useState} from 'react'
import styled from 'styled-components'

export default function OtherCardsTable() {
  const {data: paymentMethods} = usePaymentMethods()
  const {paymentMethod: defaultPaymentMethod} = useDefaultPaymentMethod()
  const [hasScheduledBroadcast, setHasScheduledBroadcast] = useState(false)
  const [responseError, setResponseError] = useState<string | null>(null)

  if (!paymentMethods) {
    return null
  }

  const otherPaymentMethods = paymentMethods.filter(
    (pm) => pm.id !== defaultPaymentMethod?.id,
  )

  if (otherPaymentMethods.length === 0) {
    return <p>No other credit cards have been added.</p>
  }

  return (
    <>
      <ErrorAlert>{responseError}</ErrorAlert>
      <ErrorAlert showing={hasScheduledBroadcast}>
        Could not remove card with an active broadcast scheduled.
      </ErrorAlert>
      <CardsTable>
        <TableHead>
          <TableRow>
            <NameCell>Name on Card</NameCell>
            <BrandCell>Brand</BrandCell>
            <Last4Cell>Last 4 Digits</Last4Cell>
            <ActionCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {otherPaymentMethods.map((paymentMethod) => (
            <TableRow key={paymentMethod.id}>
              <NameCell data-label="Name on Card">
                {paymentMethod.billing_details?.name || '—'}
              </NameCell>
              <BrandCell data-label="Brand">
                {paymentMethod.card?.brand}
              </BrandCell>
              <Last4Cell data-label="Last 4 Digits">
                {paymentMethod.card?.last4}
              </Last4Cell>
              <ActionCell data-label="">
                <ActionButtons>
                  <SetDefaultPaymentMethodButton
                    paymentMethod={paymentMethod}
                    setResponseError={setResponseError}
                  />
                  <RemovePaymentMethodButton
                    paymentMethod={paymentMethod}
                    setHasScheduledBroadcast={setHasScheduledBroadcast}
                    setResponseError={setResponseError}
                  />
                </ActionButtons>
              </ActionCell>
            </TableRow>
          ))}
        </TableBody>
      </CardsTable>
    </>
  )
}

function SetDefaultPaymentMethodButton(props: {
  paymentMethod: PaymentMethod
  setResponseError: (error: string | null) => void
}) {
  const {
    paymentMethod: {id: paymentMethodId, card},
    setResponseError,
  } = props
  const {setPaymentMethod} = useDefaultPaymentMethod()
  const pastDueRecovery = useOptionalPastDueRecovery()
  const delinquentSubscription = useGetDelinquentSubscription()
  const {isTransactionPending, runTransaction} = useBillingTransaction()
  const {mutate: setDefault, isLoading} = useSetDefaultPaymentMethod()
  const actionDisabled = isTransactionPending || isLoading

  const handleSetDefault = () => {
    if (actionDisabled) {
      return
    }

    setResponseError(null)

    runTransaction(
      () =>
        new Promise<void>((resolve, reject) => {
          setDefault(paymentMethodId, {
            onSuccess: ({user, paymentMethod}) => {
              setPaymentMethod(paymentMethod)

              if (user) {
                pastDueRecovery?.handleRecoverSuccess(user)
              }

              resolve()
            },
            onError: (error: any) => {
              const message =
                error.message || 'Unable to set default payment method.'
              pastDueRecovery?.handleRecoverError(message)
              setResponseError(message)
              reject(error)
            },
          })
        }),
    ).catch(() => {})
  }

  const brandLabel = getCardBrandDisplayName(card?.brand)
  const confirmationDescription = card?.last4
    ? `The ${brandLabel} card ending in ${card.last4} will become your default payment method and we will immediately attempt payment on your overdue subscription.`
    : `This card will become your default payment method and we will immediately attempt payment on your overdue subscription.`

  return (
    <ConfirmDialog
      confirmLabel="Set as default and pay"
      description={confirmationDescription}
      onConfirm={handleSetDefault}
      skip={!delinquentSubscription}
      title="Set as default and pay?"
      variant="info"
    >
      {(confirm) => (
        <SetDefaultSlot>
          {isLoading ? (
            <Loader />
          ) : (
            <Button
              color="primary"
              disabled={actionDisabled}
              onClick={confirm}
              aria-label={`set ${card?.brand} card ending in ${card?.last4} as default`}
              variant="outlined"
            >
              Set as default
            </Button>
          )}
        </SetDefaultSlot>
      )}
    </ConfirmDialog>
  )
}

function RemovePaymentMethodButton(props: {
  paymentMethod: PaymentMethod
  setHasScheduledBroadcast: (exists: boolean) => void
  setResponseError: (error: string | null) => void
}) {
  const {
    paymentMethod: {id: paymentMethodId, card},
    setHasScheduledBroadcast,
    setResponseError,
  } = props
  const [removing, setRemoving] = useState(false)
  const {isTransactionPending, acquireTransactionLock} = useBillingTransaction()

  const {mutate: remove} = useRemovePaymentMethod()

  const {data: broadcasts, isFetching} = useBroadcasts({
    status: 'in_future',
    paymentMethodId,
    enabled: removing,
  })

  const hasScheduledBroadcast = isFetching
    ? undefined
    : broadcasts && broadcasts.total > 0

  useEffect(() => {
    if (!removing) {
      return
    }

    return acquireTransactionLock()
  }, [removing, acquireTransactionLock])

  useEffect(() => {
    setResponseError(null)
    setHasScheduledBroadcast(false)

    if (hasScheduledBroadcast === undefined) {
      return
    }

    if (hasScheduledBroadcast) {
      setHasScheduledBroadcast(true)
      setRemoving(false)
      return
    }

    remove(paymentMethodId, {
      onError: (e: any) => {
        setResponseError(e)
        setRemoving(false)
      },
    })
  }, [
    hasScheduledBroadcast,
    setHasScheduledBroadcast,
    paymentMethodId,
    remove,
    setResponseError,
    removing,
  ])

  return (
    <ConfirmDialog
      description={`You are removing your ${card?.brand} card ending in ${card?.last4}.`}
      onConfirm={() => {
        if (isTransactionPending) {
          return
        }

        setRemoving(true)
      }}
      confirmLabel="Remove"
    >
      {(confirm) => (
        <RemoveSlot>
          {removing ? (
            <Loader />
          ) : (
            <RemoveButton
              disabled={isTransactionPending || removing}
              onClick={confirm}
              aria-label={`remove ${card?.brand} card ending in ${card?.last4}`}
            >
              Remove Card
            </RemoveButton>
          )}
        </RemoveSlot>
      )}
    </ConfirmDialog>
  )
}

const CardsTable = styled(Table)`
  table-layout: fixed;
  width: 100%;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    table-layout: auto;

    thead {
      display: none;
    }

    tbody tr {
      display: block;
      border: 1px solid ${(props) => props.theme.colors.border};
      border-radius: 12px;
      margin-bottom: ${(props) => props.theme.spacing[4]};
      padding: ${(props) =>
        `${props.theme.spacing[2]} 0 ${props.theme.spacing[1]}`};
    }

    tbody td {
      display: flex;
      align-items: center;
      width: 100% !important;
      max-width: none;
      border-bottom: none;
      padding: ${(props) =>
        `${props.theme.spacing[2]} ${props.theme.spacing[4]}`};
      white-space: normal;
      overflow: visible;
      text-overflow: unset;

      &::before {
        content: attr(data-label);
        flex: 0 0 108px;
        font-weight: 500;
        margin-right: ${(props) => props.theme.spacing[3]};
        color: ${(props) => props.theme.colors.text.primary};
      }

      &[data-label=''] {
        display: block;
        padding-top: ${(props) => props.theme.spacing[5]};
        padding-bottom: 0;

        &::before {
          display: none;
        }
      }
    }
  }
`

const NameCell = styled(TableCell)`
  width: 32%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BrandCell = styled(TableCell)`
  width: 18%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Last4Cell = styled(TableCell)`
  width: 12%;
  white-space: nowrap;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    padding-bottom: ${(props) => props.theme.spacing[1]};
  }
`

const ActionCell = styled(TableCell)`
  width: 38%;
  white-space: nowrap;
`

const SetDefaultSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 148px;
  min-height: 36px;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    min-width: 0;
    min-height: 0;
    width: 100%;
  }
`

const RemoveSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 108px;
  min-height: 36px;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    min-width: 0;
    min-height: 0;
    width: 100%;
  }
`

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${(props) => props.theme.spacing[3]};

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: ${(props) => props.theme.spacing[1]};

    .MuiButton-root {
      width: 100%;
    }
  }
`

const RemoveButton = styled(Button).attrs({
  variant: 'text',
})`
  && {
    color: ${colors.error};
    min-width: 0;
    padding: 6px 8px;
  }

  &&:hover {
    background-color: transparent;
    color: ${colors.button.danger.hover};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    && {
      padding-bottom: ${(props) => props.theme.spacing[2]};
    }
  }
`
